/**
 * Project navigation store - the open project's sections, for the sidebar.
 *
 * ProjectDetailsTabs owns the sections and which one is active (including the
 * `?tab=` links and Back); it publishes them here while a project page is open
 * and clears them when it closes. The sidebar (SideMenu / ProjectSectionNav)
 * only reads this store and calls `select`. `sections === null` means no
 * project is open, so the sidebar shows the app items.
 *
 * Not persisted: it mirrors the page that is mounted right now.
 */
import { create } from "zustand"

/** One section of the open project, as the sidebar lists it. */
export interface ProjectSection {
  /** The tab key (`?tab=<key>`) */
  key: string
  /** The section's name in the sidebar */
  label: string
}

interface ProjectNavState {
  /** The open project's sections in display order; null when no project is open */
  sections: ProjectSection[] | null
  /** Key of the section showing */
  activeKey: string | null
  /** Switches the project page to a section (the page's own tab change) */
  select: ((key: string) => void) | null

  /** Publishes the open project's sections */
  setNav: (nav: {
    sections: ProjectSection[]
    activeKey: string
    select: (key: string) => void
  }) => void
  /** Clears the sections when the project page closes */
  clearNav: () => void
}

export const useProjectNavStore = create<ProjectNavState>()((set) => ({
  sections: null,
  activeKey: null,
  select: null,

  setNav: ({ sections, activeKey, select }) => {
    set({ sections, activeKey, select })
  },
  clearNav: () => {
    set({ sections: null, activeKey: null, select: null })
  },
}))
